import {
  EditOutlined,
  LockOutlined,
  LogoutOutlined,
  SettingOutlined,
  SwitcherOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { history, useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import { message, Spin } from 'antd';
import { createStyles } from 'antd-style';
import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import { postOpenV1AccessLogout } from '@/services/swagger/kaifangjiekou';
import { postPlatV1AccessChangePwd } from '@/services/swagger/pingtaijiekou';
import HeaderDropdown from '../HeaderDropdown';
import ChangePasswordForm from './ChangePasswordForm';
import StaffSwitchModal from './StaffSwitchModal';
import UserProfileForm from './UserProfileForm';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  const { styles } = useStyles();

  // 获取用户名首字母作为头像
  const getInitials = (name?: string) => {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
  };

  return (
    <div className={styles.avatar}>
      <div className={styles.avatarIcon}>
        {getInitials(currentUser?.nickname)}
      </div>
      <span>{currentUser?.nickname}</span>
    </div>
  );
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '56px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 12px',
      cursor: 'pointer',
      borderRadius: '8px',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: 'rgba(24, 144, 255, 0.1)',
        transform: 'translateY(-1px)',
        boxShadow: '0 2px 8px rgba(24, 144, 255, 0.2)',
      },
    },
    avatar: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: token.colorText,
      fontWeight: 500,
      fontSize: '14px',
    },
    avatarIcon: {
      width: '28px',
      height: '28px',
      borderRadius: '50%',
      backgroundColor: '#1890ff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '14px',
      fontWeight: 'bold',
    },
  };
});

export const AvatarDropdown = ({ menu, children }: GlobalHeaderRightProps) => {
  /**
   * 退出登录，并且将当前的 url 保存
   */
  const loginOut = async () => {
    try {
      // 调用登出接口
      await postOpenV1AccessLogout({});
    } catch (error) {
      console.error('登出请求失败:', error);
    } finally {
      // 清除状态并跳转到登录页
      const { search, pathname } = window.location;
      const urlParams = new URL(window.location.href).searchParams;
      const searchParams = new URLSearchParams({
        redirect: pathname + search,
      });
      /** 此方法会跳转到 redirect 参数所在的位置 */
      const redirect = urlParams.get('redirect');
      // Note: There may be security issues, please note
      if (window.location.pathname !== '/login' && !redirect) {
        history.replace({
          pathname: '/login',
          search: searchParams.toString(),
        });
      }
    }
  };
  const { styles } = useStyles();

  const { initialState, setInitialState } = useModel('@@initialState');

  // 表单弹窗状态
  const [userProfileVisible, setUserProfileVisible] = useState(false);
  const [staffSwitchVisible, setStaffSwitchVisible] = useState(false);
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      loginOut();
      return;
    }
    if (key === 'update-profile') {
      setUserProfileVisible(true);
      return;
    }
    if (key === 'switch-staff') {
      setStaffSwitchVisible(true);
      return;
    }
    if (key === 'change-password') {
      setChangePasswordVisible(true);
      return;
    }
    history.push(`/account/${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;

  if (!currentUser || !currentUser.nickname) {
    return loading;
  }

  const menuItems = [
    ...(menu
      ? [
          {
            key: 'center',
            icon: <UserOutlined />,
            label: '个人中心',
          },
          {
            key: 'settings',
            icon: <SettingOutlined />,
            label: '个人设置',
          },
          {
            type: 'divider' as const,
          },
        ]
      : []),
    {
      key: 'update-profile',
      icon: <EditOutlined />,
      label: '修改用户信息',
    },
    {
      key: 'change-password',
      icon: <LockOutlined />,
      label: '修改密码',
    },
    {
      key: 'switch-staff',
      icon: <SwitcherOutlined />,
      label: '切换员工身份',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  return (
    <>
      <HeaderDropdown
        menu={{
          selectedKeys: [],
          onClick: onMenuClick,
          items: menuItems,
        }}
      >
        {children}
      </HeaderDropdown>
      <UserProfileForm
        visible={userProfileVisible}
        onCancel={() => setUserProfileVisible(false)}
        onSuccess={() => {
          setUserProfileVisible(false);
          // TODO: 刷新用户信息
        }}
      />
      <StaffSwitchModal
        visible={staffSwitchVisible}
        onCancel={() => setStaffSwitchVisible(false)}
        onSuccess={() => {
          setStaffSwitchVisible(false);
          // 刷新页面
          window.location.reload();
        }}
      />
      <ChangePasswordForm
        visible={changePasswordVisible}
        onCancel={() => setChangePasswordVisible(false)}
        onSuccess={async (values) => {
          await postPlatV1AccessChangePwd({
            oldPassword: values.oldPassword,
            newPassword: values.newPassword,
          });
          message.success('密码修改成功');
          // ✅ 只更新 UI 状态，让全局 errorHandler 显示错误
          setChangePasswordVisible(false);
        }}
      />
    </>
  );
};
